// src/pages/SalesManagement.js

import React, { useState, useEffect } from 'react';
import {
  Box,
  Badge,
  Button,
  Card,
  CardActions,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Checkbox,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  NotificationImportant as NotificationImportantIcon,
  Payment as PaymentIcon,
} from '@mui/icons-material';

const API_URL           = "https://htm.bayex.lk/api/sales_management.php";
const CUSTOMERS_API_URL = "https://htm.bayex.lk/api/get_customers1.php";
const ADD_CUSTOMER_URL  = "https://htm.bayex.lk/api/add_customer.php";

const paymentMethodOptions = [
  { label: "Cash",   value: "cash"   },
  { label: "Card",   value: "card"   },
  { label: "Online", value: "online" },
];
const paymentStatusOptions = [
  { label: "Credit",    value: "Credit"    },
  { label: "Paid",      value: "paid"      },
  { label: "Cancelled", value: "cancelled" },
];
const statusColors = {
  paid:      "#e8f5e9",
  Credit:    "#fff8e1",
  cancelled: "#ffebee",
};

export default function SalesManagement() {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // date-range filter
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo,   setFilterTo]   = useState('');
  const [sales,      setSales]      = useState([]);
  const [customerList, setCustomerList] = useState([]);

  // modal & form
  const [openModal,   setOpenModal]   = useState(false);
  const [isEditMode,  setIsEditMode]  = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [updatingId,  setUpdatingId]  = useState(null);
  const [saleData,    setSaleData]    = useState({
    id: null,
    order_details:   '',
    amount:          '',
    payment_method:  '',
    payment_status:  '',
    customer_id:     '',
    _oldStatus:      '',
    date:            new Date().toISOString().split('T')[0],
  });

  // new-customer dialog
  const [newCustomerOpen, setNewCustomerOpen] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState({ name:'', email:'', phone:'' });

  // receive-payment dialog
  const [openPayDialog, setOpenPayDialog] = useState(false);
  const [payCustomer,   setPayCustomer]   = useState(null);
  const [dueSales,      setDueSales]      = useState([]);
  const [selectedIds,   setSelectedIds]   = useState(new Set());
  const [processingPay, setProcessingPay] = useState(false);

  // fetch sales
  const fetchSales = () => {
    const params = new URLSearchParams();
    if (filterFrom) params.append('date_from', filterFrom);
    if (filterTo)   params.append('date_to',   filterTo);
    fetch(`${API_URL}?${params}`)
      .then(r=>r.json())
      .then(setSales)
      .catch(console.error);
  };
  // fetch customers
  const fetchCustomers = () => {
    fetch(CUSTOMERS_API_URL)
      .then(r=>r.json())
      .then(setCustomerList)
      .catch(console.error);
  };

  useEffect(() => {
    fetchSales();
    fetchCustomers();
  }, []);
  useEffect(fetchSales, [filterFrom, filterTo]);

  // add/edit dialog
  const openDialog = sale => {
    if (sale) {
      setSaleData({
        id:             sale.id,
        order_details:  sale.order_details,
        amount:         sale.amount,
        payment_method: sale.payment_method,
        payment_status: sale.payment_status,
        customer_id:    sale.customer,
        _oldStatus:     sale.payment_status,
        date:           sale.created_at.split('T')[0],
      });
      setIsEditMode(true);
    } else {
      setSaleData({
        id: null,
        order_details: '',
        amount:        '',
        payment_method:'',
        payment_status:'',
        customer_id:   '',
        _oldStatus:    '',
        date:          new Date().toISOString().split('T')[0],
      });
      setIsEditMode(false);
    }
    setOpenModal(true);
  };
  const closeDialog = () => setOpenModal(false);

  // input change
  const onInputChange = e => {
    const { name, value } = e.target;
    setSaleData(d => ({ ...d, [name]: value }));
  };

  const getCustomerName = id => {
    const c = customerList.find(x => x.id == id);
    return c ? c.name : id;
  };

  // save add/update
  const saveSale = () => {
    setSaving(true);
    const payload = {
      action:       isEditMode ? 'update' : 'add',
      ...saleData,
      created_at:   saleData.date,
      amount:       parseFloat(saleData.amount) || 0,
      customer:     saleData.customer_id,
    };
    fetch(API_URL, {
      method:'POST',
      headers:{ 'Content-Type':'application/json' },
      body: JSON.stringify(payload),
    })
    .then(r=>r.json())
    .then(data=>{
      setSaving(false);
      if (data.status==='success') {
        fetchSales();
        closeDialog();
      } else {
        console.error('Save error:', data.message);
      }
    })
    .catch(err=>{
      setSaving(false);
      console.error(err);
    });
  };

  // delete sale
  const deleteSale = id => {
    if (!window.confirm('Delete this sale?')) return;
    fetch(API_URL,{
      method:'POST',
      headers:{ 'Content-Type':'application/json' },
      body: JSON.stringify({ action:'delete', id })
    })
    .then(r=>r.json())
    .then(d=>{ if(d.status==='success') fetchSales(); else console.error(d.message); })
    .catch(console.error);
  };

  // mark single paid
  const markAsPaid = sale => {
    if (sale.payment_status.toLowerCase()==='paid') return;
    if (!window.confirm('Mark this sale as PAID?')) return;
    setUpdatingId(sale.id);
    const payload = {
      action: 'update',
      id: sale.id,
      order_details: sale.order_details,
      amount: sale.amount,
      payment_method: sale.payment_method,
      payment_status: 'paid',
      customer: sale.customer,
      created_at: sale.created_at.split('T')[0],
    };
    fetch(API_URL,{
      method:'POST',
      headers:{ 'Content-Type':'application/json' },
      body: JSON.stringify(payload)
    })
    .then(r=>r.json())
    .then(d=>{
      setUpdatingId(null);
      if (d.status==='success') fetchSales();
      else console.error('Paid update failed:', d.message);
    })
    .catch(err=>{
      setUpdatingId(null);
      console.error(err);
    });
  };

  // send reminder
  const sendReminder = sale => {
    let phone = sale.customer_phone
      || customerList.find(c=>c.id==sale.customer)?.phone;
    if (!phone) return alert('No phone on record.');
    if (!window.confirm('Send payment reminder?')) return;

    const custName = getCustomerName(sale.customer);
    const invNo    = sale.order_details;
    const datePart = sale.created_at ? sale.created_at.split('T')[0] : filterFrom || '—';
    const msg = `Dear ${custName}!, a kind reminder to clear Rs.${sale.amount} ` +
                `(Invoice #${invNo}, ${datePart}).\n` +
                `If paying online:\nPizza Hub, 1000208076, Commercial Bank.\n` +
                `Please Note: invoice no. ${invNo}`;

    fetch(
      `https://htm.bayex.lk/api/send_sms.php?to=${encodeURIComponent(phone)}` +
      `&text=${encodeURIComponent(msg)}`
    )
    .then(r=>r.text())
    .then(txt=>alert("SMS response: "+txt))
    .catch(console.error);
  };

  // add customer
  const addCustomer = () => {
    const { name, phone, email } = newCustomerData;
    if (!name||!phone) return alert("Name & phone required.");
    fetch(ADD_CUSTOMER_URL,{
      method:'POST',
      headers:{ 'Content-Type':'application/json' },
      body: JSON.stringify(newCustomerData)
    })
    .then(r=>r.json())
    .then(d=>{
      if (d.status==='success') {
        fetchCustomers();
        setSaleData(s=>({...s,customer_id:d.id}));
        setNewCustomerOpen(false);
      } else alert("Error: "+d.message);
    })
    .catch(console.error);
  };

  // ─── Receive Payment handlers ──────────────────────
  const openPay = () => {
    setPayCustomer(null);
    setDueSales([]);
    setSelectedIds(new Set());
    setOpenPayDialog(true);
  };
  const closePay = () => setOpenPayDialog(false);

  useEffect(() => {
    if (!payCustomer) {
      setDueSales([]);
      return;
    }
    const dues = sales.filter(s =>
      s.customer === payCustomer.id &&
      s.payment_status.toLowerCase() === 'credit'
    );
    setDueSales(dues);
    setSelectedIds(new Set());
  }, [payCustomer, sales]);

  const toggleSelect = id => {
    setSelectedIds(ids => {
      const nxt = new Set(ids);
      if (nxt.has(id)) nxt.delete(id);
      else nxt.add(id);
      return nxt;
    });
  };

  // corrected bulkPay: include all required fields
  const bulkPay = ids => {
    if (!ids.length) return;
    if (!window.confirm(`Mark ${ids.length} invoices as paid?`)) return;
    setProcessingPay(true);
    Promise.all(ids.map(id => {
      const sale = dueSales.find(s => s.id === id);
      if (!sale) return Promise.resolve();
      const payload = {
        action:         'update',
        id:             sale.id,
        order_details:  sale.order_details,
        amount:         sale.amount,
        payment_method: sale.payment_method,
        payment_status: 'paid',
        customer:       sale.customer,
        created_at:     sale.created_at.split('T')[0],
      };
      return fetch(API_URL, {
        method:'POST',
        headers:{ 'Content-Type':'application/json' },
        body: JSON.stringify(payload)
      }).then(r=>r.json());
    }))
    .then(() => {
      setProcessingPay(false);
      fetchSales();
      closePay();
    })
    .catch(err => {
      console.error(err);
      setProcessingPay(false);
      alert('Error paying invoices');
    });
  };

  const totalDue = dueSales.reduce((sum,s) => sum + parseFloat(s.amount||0), 0);
  const selectedTotal = dueSales
    .filter(s => selectedIds.has(s.id))
    .reduce((sum,s) => sum + parseFloat(s.amount||0), 0);

  return (
    <Box sx={{ p:2 }}>
      <Typography variant="h4" gutterBottom>Credit Management</Typography>

      {/* date-range filter + Receive Payment */}
      <Box sx={{ display:'flex', gap:2, mb:2, flexWrap:'wrap' }}>
        <TextField
          label="From Date" type="date" size="small"
          InputLabelProps={{ shrink:true }}
          value={filterFrom}
          onChange={e=>setFilterFrom(e.target.value)}
        />
        <TextField
          label="To Date" type="date" size="small"
          InputLabelProps={{ shrink:true }}
          value={filterTo}
          onChange={e=>setFilterTo(e.target.value)}
        />
        <Button
          variant="contained"
          startIcon={<RefreshIcon/>}
          onClick={fetchSales}
        >
          Apply Filter
        </Button>
        <Box flexGrow={1}/>
        <Button
          variant="contained"
          startIcon={<PaymentIcon/>}
          onClick={openPay}
        >
          Receive Payment
        </Button>
      </Box>

      {/* "Record New Sale" */}
      <Box sx={{ mb:2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon/>}
          onClick={()=>openDialog(null)}
        >
          Record New Sale
        </Button>
      </Box>

      {/* Sales list */}
      {isMobile ? (
        <Box sx={{ display:'flex', flexDirection:'column', gap:2 }}>
          {sales.length===0
            ? <Typography align="center">No records found.</Typography>
            : sales.map(sale=>(
              <Card key={sale.id} variant="outlined"
                sx={{ backgroundColor: statusColors[sale.payment_status] || 'inherit' }}
              >
                <CardContent>
                  <Typography><strong>#{sale.id}</strong> {sale.order_details}</Typography>
                  <Typography>Cust: {getCustomerName(sale.customer)}</Typography>
                  <Typography>Amt: {sale.amount}</Typography>
                  <Typography>{sale.payment_method} / {sale.payment_status}</Typography>
                </CardContent>
                <CardActions>
                  <IconButton onClick={()=>openDialog(sale)}><EditIcon/></IconButton>
                  <IconButton onClick={()=>deleteSale(sale.id)} color="error"><DeleteIcon/></IconButton>
                  <Badge badgeContent={sale.reminder_count||0} color="error">
                    <IconButton onClick={()=>sendReminder(sale)} color="secondary">
                      <NotificationImportantIcon/>
                    </IconButton>
                  </Badge>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={sale.payment_status.toLowerCase()==='paid' || updatingId===sale.id}
                    onClick={()=>markAsPaid(sale)}
                    startIcon={updatingId===sale.id && <CircularProgress size={16}/>}
                  >
                    Paid
                  </Button>
                </CardActions>
              </Card>
            ))
          }
        </Box>
      ) : (
        <Paper>
          <Table>
            <TableHead>
              <TableRow>
                {["ID","Ref#","Customer","Amount","Method","Status","Actions"].map(h=>(
                  <TableCell key={h}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sales.length===0
                ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">No records found.</TableCell>
                  </TableRow>
                ) : sales.map(sale=>(
                  <TableRow key={sale.id}
                    sx={{ backgroundColor: statusColors[sale.payment_status] || 'inherit' }}
                  >
                    <TableCell>{sale.id}</TableCell>
                    <TableCell>{sale.order_details}</TableCell>
                    <TableCell>{getCustomerName(sale.customer)}</TableCell>
                    <TableCell>{sale.amount}</TableCell>
                    <TableCell>{sale.payment_method}</TableCell>
                    <TableCell>{sale.payment_status}</TableCell>
                    <TableCell>
                      <IconButton onClick={()=>openDialog(sale)}><EditIcon/></IconButton>
                      <IconButton onClick={()=>deleteSale(sale.id)} color="error"><DeleteIcon/></IconButton>
                      <Badge badgeContent={sale.reminder_count||0} color="error">
                        <IconButton onClick={()=>sendReminder(sale)} color="secondary">
                          <NotificationImportantIcon/>
                        </IconButton>
                      </Badge>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={sale.payment_status.toLowerCase()==='paid' || updatingId===sale.id}
                        onClick={()=>markAsPaid(sale)}
                        startIcon={updatingId===sale.id && <CircularProgress size={16}/>}
                        sx={{ ml:1 }}
                      >
                        Paid
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Add/Edit Sale Dialog */}
      <Dialog open={openModal} onClose={closeDialog} fullWidth>
        <DialogTitle>{isEditMode? "Edit Sale" : "Record New Sale"}</DialogTitle>
        <DialogContent sx={{ display:'flex', flexDirection:'column', gap:2 }}>
          <TextField
            label="Date"
            type="date"
            name="date"
            InputLabelProps={{ shrink:true }}
            value={saleData.date}
            onChange={onInputChange}
            variant="standard"
            fullWidth
          />

          <Autocomplete
            options={customerList}
            getOptionLabel={c=>`${c.name} (${c.phone})`}
            value={customerList.find(c=>c.id==saleData.customer_id)||null}
            onChange={(_,v)=>setSaleData(d=>({...d,customer_id:v?.id||''}))}
            renderInput={params=>(
              <TextField {...params} label="Customer" variant="standard" fullWidth />
            )}
          />
          <Box sx={{ textAlign:'right', mb:1 }}>
            <Button size="small" variant="text" onClick={()=>setNewCustomerOpen(true)}>
              + Add New Customer
            </Button>
          </Box>

          <TextField
            name="order_details"
            label="Order Ref. Number"
            variant="standard"
            fullWidth
            value={saleData.order_details}
            onChange={onInputChange}
          />
          <TextField
            name="amount"
            label="Amount"
            type="number"
            variant="standard"
            fullWidth
            value={saleData.amount}
            onChange={onInputChange}
          />
          <TextField
            select
            name="payment_method"
            label="Payment Method"
            variant="standard"
            fullWidth
            value={saleData.payment_method}
            onChange={onInputChange}
          >
            {paymentMethodOptions.map(o=>(
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            name="payment_status"
            label="Payment Status"
            variant="standard"
            fullWidth
            value={saleData.payment_status}
            onChange={onInputChange}
          >
            {paymentStatusOptions.map(o=>(
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>Cancel</Button>
          <Button
            onClick={saveSale}
            variant="contained"
            disabled={saving}
            startIcon={saving && <CircularProgress size={20}/>}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add New Customer Dialog */}
      <Dialog open={newCustomerOpen} onClose={()=>setNewCustomerOpen(false)} fullWidth>
        <DialogTitle>Add New Customer</DialogTitle>
        <DialogContent sx={{ display:'flex', flexDirection:'column', gap:2 }}>
          <TextField
            label="Name"
            variant="standard"
            fullWidth
            value={newCustomerData.name}
            onChange={e=>setNewCustomerData(d=>({...d,name:e.target.value}))}
          />
          <TextField
            label="Email"
            variant="standard"
            fullWidth
            value={newCustomerData.email}
            onChange={e=>setNewCustomerData(d=>({...d,email:e.target.value}))}
          />
          <TextField
            label="Phone"
            variant="standard"
            fullWidth
            value={newCustomerData.phone}
            onChange={e=>setNewCustomerData(d=>({...d,phone:e.target.value}))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setNewCustomerOpen(false)}>Cancel</Button>
          <Button onClick={addCustomer} variant="contained">Add Customer</Button>
        </DialogActions>
      </Dialog>

      {/* Receive Payment Dialog */}
      <Dialog open={openPayDialog} onClose={closePay} maxWidth="md" fullWidth>
        <DialogTitle>Receive Payment</DialogTitle>
        <DialogContent sx={{ display:'flex', flexDirection:'column', gap:2 }}>
          <Autocomplete
            options={customerList}
            getOptionLabel={c=>`${c.name} (${c.phone})`}
            value={payCustomer}
            onChange={(_,c)=>setPayCustomer(c)}
            renderInput={params => (
              <TextField {...params} label="Select Customer" variant="outlined" fullWidth/>
            )}
          />

          {payCustomer && (
            <>
              <Typography variant="subtitle1">
                Outstanding invoices: {dueSales.length} &nbsp;|&nbsp;
                Total Due: Rs {totalDue.toFixed(2)} &nbsp;|&nbsp;
                Selected Total: Rs {selectedTotal.toFixed(2)}
              </Typography>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={40}>
                      <Checkbox
                        indeterminate={
                          selectedIds.size > 0 &&
                          selectedIds.size < dueSales.length
                        }
                        checked={
                          dueSales.length > 0 &&
                          selectedIds.size === dueSales.length
                        }
                        onChange={e => {
                          if (e.target.checked) {
                            setSelectedIds(new Set(dueSales.map(s=>s.id)));
                          } else {
                            setSelectedIds(new Set());
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>Invoice #</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dueSales.map(s => (
                    <TableRow key={s.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.has(s.id)}
                          onChange={()=>toggleSelect(s.id)}
                        />
                      </TableCell>
                      <TableCell>{s.order_details}</TableCell>
                      <TableCell>{s.created_at.split('T')[0]}</TableCell>
                      <TableCell align="right">
                        Rs {parseFloat(s.amount).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePay} disabled={processingPay}>
            Cancel
          </Button>
          <Button
            onClick={()=>bulkPay(dueSales.map(s=>s.id))}
            disabled={!dueSales.length || processingPay}
          >
            {processingPay ? 'Processing…' : 'Pay All'}
          </Button>
          <Button
            onClick={()=>bulkPay(Array.from(selectedIds))}
            disabled={!selectedIds.size || processingPay}
          >
            {processingPay ? 'Processing…' : 'Pay Selected'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
